
import axios from 'axios'
import { format } from 'date-fns'
import { defineComponent, inject, reactive, ref, computed } from 'vue'
import { AuthPlugin } from '@/auth'
import { Download } from '@element-plus/icons-vue'

// todo: 定義箇所をまとめる
interface PredictionResult {
  BMS: number
  BMS_L2ALLG74: number
  BMS_L2ALLGL: number
  BMS_L2G74: number
  BMS_L2GL: number
  BMS_L3ALLG74: number
  BMS_L3ALLGL: number
  BMS_L3G74: number
  BMS_L3GL: number
}

interface UserResult {
  name: string
}

interface Prediction {
  id: number
  user: UserResult
  userId: string
  cattleId: string
  farmer?: string
  shotAt?: string
  shootingDate: string
  input: unknown
  result: PredictionResult
  createdAt?: string
  updatedAt?: string
  hasCarcass?: boolean
}

interface PaginationInfo {
  page: number
  perPage: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

interface PaginationInfoResponse {
  "page": number
  "per_page": number
  "total": number
  "total_pages": number
  "has_next": boolean
  "has_prev": boolean
}

interface PredictionListResponse {
  data: Prediction[]
  pagination: PaginationInfoResponse
}

// テーブルのソートカラム名とバックエンドのsort_byパラメータのマッピング
const sortColumnMap: Record<string, string> = {
  'updatedAt': 'updated_at',
  'shotAt': 'shot_at',
  'result.BMS': 'result.BMS'
}

export default defineComponent({
  name: 'List',
  setup() {
    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }
    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }
    const isAdminUser = inject('IsAdminUser')
    if (isAdminUser === undefined) {
      throw new Error('IsAdminUser is not provided')
    }
    const predictions = reactive<Prediction[]>([])
    
    // ページネーション関連の状態
    const currentPage = ref(1)
    const perPage = ref(20)
    const sortBy = ref('created_at')
    const sortOrder = ref<'asc' | 'desc'>('desc')
    const pagination = ref<PaginationInfo | null>(null)
    
    // フィルター関連の状態
    const filterCreatedAtRange = ref<[string, string] | null>(null)
    const filterShotAtRange = ref<[string, string] | null>(null)
    const filterCattleId = ref<string>('')
    const filterFarmer = ref<string>('')
    
    // テーブルの再描画用のキー（ソート状態変更時に更新）
    const tableKey = ref(0)

    // テーブルのソート状態を管理（バックエンドのソート状態と同期）
    const tableSort = computed(() => {
      // バックエンドのsort_byをテーブルのカラム名に変換
      const reverseSortColumnMap: Record<string, string> = {
        'updated_at': 'updatedAt',
        'shot_at': 'shotAt',
        'result.BMS': 'result.BMS',
        'created_at': 'updatedAt' // デフォルトはupdatedAtで表示
      }
      const column = reverseSortColumnMap[sortBy.value] || 'updatedAt'
      const order = sortOrder.value === 'asc' ? 'ascending' : 'descending'
      return { prop: column, order }
    })

    const formatDate = (date: string, pattern = 'yyyy-MM-dd HH:mm:ss') => {
      return format(new Date(date), pattern)
    }

    const getPredictions = async () => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw

      // 代理ログイン用のヘッダーを追加
      const headers: Record<string, string> = { Authorization: `Bearer ${idToken}` }
      const impersonationToken = localStorage.getItem('impersonationToken')
      if (impersonationToken) {
        headers['X-AIMEAT-Impersonation-Token'] = impersonationToken
      }

      const params: Record<string, string | number> = {
        page: currentPage.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_order: sortOrder.value
      }

      // フィルターパラメータの追加
      if (filterCreatedAtRange.value && filterCreatedAtRange.value.length === 2) {
        params.created_at_from = filterCreatedAtRange.value[0]
        params.created_at_to = filterCreatedAtRange.value[1]
      }
      if (filterShotAtRange.value && filterShotAtRange.value.length === 2) {
        params.shot_at_from = filterShotAtRange.value[0]
        params.shot_at_to = filterShotAtRange.value[1]
      }
      if (filterCattleId.value) {
        params.cattle_id = filterCattleId.value
      }
      if (filterFarmer.value) {
        params.farmer = filterFarmer.value
      }

      const response = await axios.get<PredictionListResponse>(`${backendUrl}/v1/prediction`, {
        headers,
        params,
      })

      // レスポンス形式の変更に対応
      predictions.splice(0, predictions.length, ...response.data.data)
      
      // バックエンドのスネークケースをキャメルケースに変換
      const paginationResponse = response.data.pagination
      pagination.value = {
        page: paginationResponse.page,
        perPage: paginationResponse.per_page,
        total: paginationResponse.total,
        totalPages: paginationResponse.total_pages,
        hasNext: paginationResponse.has_next,
        hasPrev: paginationResponse.has_prev
      }
      
      predictions.forEach((prediction) => {
        if (prediction.updatedAt) {
          prediction.updatedAt = formatDate(prediction.updatedAt)
        }
        if (prediction.shotAt) {
          prediction.shotAt = formatDate(prediction.shotAt)
        } else {
          prediction.shotAt = formatDate(prediction.shootingDate, 'yyyy-MM-dd')
        }
      })
      
      // テーブルを再描画してソート状態を反映
      tableKey.value++
    }
    
    // 初期データ取得
    getPredictions()

    // ページサイズ変更時のハンドラー
    const handleSizeChange = (val: number) => {
      perPage.value = val
      currentPage.value = 1 // ページサイズ変更時は1ページ目に戻す
      getPredictions()
    }

    // 現在のページ変更時のハンドラー
    const handleCurrentChange = (val: number) => {
      currentPage.value = val
      getPredictions()
    }

    // テーブルのソート変更時のハンドラー
    const handleSortChange = ({ prop, order }: { prop: string; order: string | null }) => {
      if (!order) {
        // ソート解除時はデフォルトに戻す
        sortBy.value = 'created_at'
        sortOrder.value = 'desc'
      } else {
        // テーブルのカラム名をバックエンドのsort_byパラメータに変換
        const backendSortBy = sortColumnMap[prop] || 'created_at'
        sortBy.value = backendSortBy
        sortOrder.value = order === 'ascending' ? 'asc' : 'desc'
      }
      currentPage.value = 1 // ソート変更時は1ページ目に戻す
      getPredictions()
    }

    // フィルター変更時のハンドラー（入力中は即時適用しない）
    const handleFilterChange = () => {
      // 入力中は何もしない（適用ボタンで適用）
    }

    // フィルター適用
    const applyFilters = () => {
      currentPage.value = 1 // フィルター適用時は1ページ目に戻す
      getPredictions()
    }

    // フィルタークリア
    const clearFilters = () => {
      filterCreatedAtRange.value = null
      filterShotAtRange.value = null
      filterCattleId.value = ''
      filterFarmer.value = ''
      currentPage.value = 1 // フィルタークリア時は1ページ目に戻す
      getPredictions()
    }

    const downloadCsv = async () => {
      const filename = `result_${format(new Date(), 'yyyyMMddHHmmss')}.csv`
      let content =
        ['解析日時', 'ユーザー名', '個体識別番号', '撮影日時', '推定BMS', '飼養管理者'].join(',') + '\n'
      predictions.forEach((pred) => {
        const username = pred.user ? pred.user.name : '-'
        const line =
          [pred.updatedAt, username, pred.cattleId, pred.shotAt, pred.result.BMS, pred.farmer].join(',') + '\n'
        content += line
      })
      content = '\ufeff' + content // BOM付与(Excel文字化け対策)
      const blob = new Blob([content], { type: 'text/csv' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
    }

    return {
      predictions,
      downloadCsv,
      Download,
      isAdminUser,
      currentPage,
      perPage,
      pagination,
      tableSort,
      tableKey,
      filterCreatedAtRange,
      filterShotAtRange,
      filterCattleId,
      filterFarmer,
      handleSizeChange,
      handleCurrentChange,
      handleSortChange,
      handleFilterChange,
      applyFilters,
      clearFilters,
    }
  },
})
